/**
 * Replaces the sort <select> with a styled listbox.
 *
 * A native select's popup is drawn by the OS, so CSS cannot reach it.
 * The native element stays in the DOM -- it carries the form value and
 * is what Dawn's facet code listens to -- and this renders a parallel
 * listbox over it, writing back through .value + a 'change' event.
 *
 * Progressive enhancement: with JS off the native select still works.
 */
(function () {
  const build = (select) => {
    if (select.dataset.customSelect) return;
    select.dataset.customSelect = 'true';

    const wrap = document.createElement('div');
    wrap.className = 'cselect';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cselect__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.className = 'cselect__label';
    button.appendChild(label);

    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('viewBox', '0 0 12 8');
    caret.setAttribute('class', 'cselect__caret');
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';
    button.appendChild(caret);

    const list = document.createElement('ul');
    list.className = 'cselect__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    const options = [...select.options];
    const sync = () => {
      label.textContent = select.options[select.selectedIndex]?.textContent.trim() ?? '';
      [...list.children].forEach((li, i) => {
        li.setAttribute('aria-selected', i === select.selectedIndex ? 'true' : 'false');
      });
    };

    options.forEach((opt, i) => {
      const li = document.createElement('li');
      li.className = 'cselect__option';
      li.setAttribute('role', 'option');
      li.tabIndex = -1;
      li.textContent = opt.textContent.trim();
      li.addEventListener('click', () => {
        select.selectedIndex = i;
        // Dawn's facet form listens for change; dispatching keeps sorting working.
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        close();
        button.focus();
      });
      list.appendChild(li);
    });

    const open = () => {
      list.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      list.children[select.selectedIndex]?.focus();
    };
    const close = () => {
      list.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    };
    const toggle = () => (list.hidden ? open() : close());

    button.addEventListener('click', toggle);

    wrap.addEventListener('keydown', (e) => {
      const items = [...list.children];
      const at = items.indexOf(document.activeElement);
      if (e.key === 'Escape') { close(); button.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) open(); else items[Math.min(at + 1, items.length - 1)]?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); items[Math.max(at - 1, 0)]?.focus(); }
      else if ((e.key === 'Enter' || e.key === ' ') && !list.hidden && at > -1) { e.preventDefault(); items[at].click(); }
    });

    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target)) close();
    });

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(button);
    wrap.appendChild(list);
    wrap.appendChild(select);
    sync();
    select.addEventListener('change', sync);
  };

  const scan = () => document.querySelectorAll('select[name="sort_by"]').forEach(build);

  document.addEventListener('DOMContentLoaded', scan);
  // Facet updates replace the grid and its controls without a page load.
  document.addEventListener('shopify:section:load', scan);
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState !== 'loading') scan();
})();
